/**
 * QASettingsTab - surfaces the self-learning knobs that previously lived
 * only inside Frappe Desk.
 *
 * Exposes the editable subset of Orgain QA Settings + Orgain Brain Settings:
 *   - QA majority-vote thresholds (auto_correction_threshold etc.)
 *   - Document semantic reviewer config (enable + model + confidence floor)
 *   - Classifier agent config (idle window + confidence floor/delta)
 *   - Email digest config
 *
 * Larger Brain Settings concerns (Qdrant/Neo4j URIs, embedding models) are
 * intentionally NOT exposed here - those stay in Frappe Desk to avoid
 * accidental ops changes from the admin UI.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Save, RotateCcw } from 'lucide-react';
import {
  entityQAService,
  brainConfigService,
  type SelfLearningSettingsResponse,
  type SelfLearningSection,
  type SelfLearningFieldSpec,
  type LlmModelSummary,
} from '@/services/kbService';
import { logger } from '@/utils/logger';

// Reviewer provider choices - mirrors the backend doctype Select options
// (openai\nanthropic\ngemini) and the LLM Routing tab's provider list.
const REVIEWER_PROVIDERS: Array<{ value: string; label: string }> = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'gemini', label: 'Gemini' },
];

// React provider slug → lowercased LLM Provider record key used to index
// the models map. Mirrors KBSettingsPanel: gemini → google, else the slug.
const providerKeyFor = (slug: unknown): string => {
  const s = String(slug || '').toLowerCase();
  return s === 'gemini' ? 'google' : s;
};

// A dropdown option pair. When set on a FieldRow, the field renders as a
// <Select> instead of the default text/number input.
interface SelectOption {
  value: string;
  label: string;
}

const FIELD_LABELS: Record<string, string> = {
  // QA settings
  enable_entity_qa: 'Enable Entity QA pipeline',
  auto_correction_threshold: 'Auto-correction threshold',
  low_confidence_threshold: 'Low-confidence flag threshold',
  max_issues_per_document: 'Max issues per document',
  enable_notifications: 'Enable QA notifications',
  notify_on_critical_issues: 'Notify on critical issues',
  notify_on_quality_drop: 'Notify on quality drop',
  notification_email: 'Notification email',
  // Brain settings - reviewer
  reviewer_enabled: 'Enable semantic reviewer (Stage 9.2)',
  reviewer_provider: 'Reviewer provider',
  reviewer_model: 'Reviewer model',
  reviewer_confidence_floor: 'Reviewer confidence floor',
  // Brain settings - classifier
  classifier_agent_enabled: 'Enable classifier agent',
  classifier_agent_idle_window_minutes: 'Classifier idle window (min)',
  classifier_agent_max_wait_minutes: 'Classifier max wait (min)',
  classifier_agent_confidence_floor: 'Classifier regex confidence floor',
  classifier_agent_confidence_delta: 'Classifier LLM override delta',
};

const SECTION_TITLES: Record<string, { title: string; subtitle: string }> = {
  qa_settings: {
    title: 'Entity QA & Majority-Vote Auto-Correction',
    subtitle:
      'Cross-document consensus rules. Lower the auto-correction threshold to allow weaker majorities (e.g. 5-of-6) to override outlier labels.',
  },
  brain_settings: {
    title: 'Document Semantic Reviewer & Classifier Agent',
    subtitle:
      'Per-upload guardrails. Reviewer (Stage 9.2) does whole-document re-judgement of every extraction. Classifier Agent re-extracts misclassified docs in the background.',
  },
};

interface FieldDraft {
  value: string | number | boolean | null;
  spec: SelfLearningFieldSpec;
}

type DraftMap = Record<string, Record<string, FieldDraft>>;

function buildInitialDraft(resp: SelfLearningSettingsResponse): DraftMap {
  const out: DraftMap = {};
  for (const [sectionKey, section] of Object.entries(resp.sections)) {
    if (!section) continue;
    out[sectionKey] = {};
    for (const [fname, spec] of Object.entries(section.fields)) {
      out[sectionKey][fname] = { value: spec.value, spec };
    }
  }
  return out;
}

function hasDraftChanges(draft: DraftMap, baseline: DraftMap): boolean {
  for (const sectionKey of Object.keys(draft)) {
    const sec = draft[sectionKey] || {};
    const base = baseline[sectionKey] || {};
    for (const fname of Object.keys(sec)) {
      if (sec[fname].value !== base[fname]?.value) return true;
    }
  }
  return false;
}

function diffPayload(draft: DraftMap, baseline: DraftMap) {
  const out: Record<string, Record<string, unknown>> = {};
  for (const sectionKey of Object.keys(draft)) {
    const sec = draft[sectionKey] || {};
    const base = baseline[sectionKey] || {};
    const changed: Record<string, unknown> = {};
    for (const fname of Object.keys(sec)) {
      if (sec[fname].value !== base[fname]?.value) {
        changed[fname] = sec[fname].value;
      }
    }
    if (Object.keys(changed).length) {
      out[sectionKey] = changed;
    }
  }
  return out;
}

interface FieldRowProps {
  fieldName: string;
  draft: FieldDraft;
  onChange: (value: string | number | boolean | null) => void;
  /** When provided, render the field as a dropdown instead of a text input. */
  selectOptions?: SelectOption[];
  /** Placeholder for the select / empty-options hint. */
  selectPlaceholder?: string;
}

function FieldRow({ fieldName, draft, onChange, selectOptions, selectPlaceholder }: FieldRowProps) {
  const { spec } = draft;
  const label = FIELD_LABELS[fieldName] || fieldName;
  const description = spec.description;

  // Dropdown fields (e.g. Reviewer provider / model) - render a Select so the
  // operator picks from valid values instead of free-typing a provider/model.
  if (selectOptions) {
    // A value that isn't among the options (e.g. a model belonging to a
    // different provider) is treated as unset - Radix shows the placeholder,
    // prompting a valid pick instead of displaying a stale/invalid selection.
    const raw = draft.value === null || draft.value === undefined ? '' : String(draft.value);
    const current = selectOptions.some((o) => o.value === raw) ? raw : '';
    return (
      <div className="py-3">
        <Label htmlFor={fieldName} className="text-sm font-medium">
          {label}
        </Label>
        {description && (
          <p className="text-xs text-muted-foreground mt-1 mb-2">{description}</p>
        )}
        <Select value={current} onValueChange={(v) => onChange(v)}>
          <SelectTrigger id={fieldName} className="max-w-md">
            <SelectValue placeholder={selectPlaceholder || 'Select…'} />
          </SelectTrigger>
          <SelectContent className="dark">
            {selectOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (spec.type === 'bool') {
    return (
      <div className="flex items-start justify-between gap-4 py-3">
        <div className="flex-1 min-w-0">
          <Label htmlFor={fieldName} className="text-sm font-medium">
            {label}
          </Label>
          {description && (
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        <Switch
          id={fieldName}
          checked={!!draft.value && draft.value !== 0}
          onCheckedChange={(checked) => onChange(checked ? 1 : 0)}
        />
      </div>
    );
  }

  const inputType = spec.type === 'str' ? 'text' : 'number';
  const stepFor = spec.type === 'float' ? 0.01 : 1;

  return (
    <div className="py-3">
      <Label htmlFor={fieldName} className="text-sm font-medium">
        {label}
      </Label>
      {description && (
        <p className="text-xs text-muted-foreground mt-1 mb-2">{description}</p>
      )}
      <Input
        id={fieldName}
        type={inputType}
        step={inputType === 'number' ? stepFor : undefined}
        min={spec.min ?? undefined}
        max={spec.max ?? undefined}
        value={draft.value === null || draft.value === undefined ? '' : String(draft.value)}
        onChange={(e) => {
          const raw = e.target.value;
          if (spec.type === 'str') {
            onChange(raw);
          } else if (raw === '') {
            onChange(null);
          } else if (spec.type === 'int') {
            const n = parseInt(raw, 10);
            onChange(Number.isFinite(n) ? n : null);
          } else if (spec.type === 'float') {
            const n = parseFloat(raw);
            onChange(Number.isFinite(n) ? n : null);
          } else {
            onChange(raw);
          }
        }}
        className="max-w-md"
      />
      {(spec.min !== null || spec.max !== null) && spec.type !== 'str' && (
        <p className="text-[11px] text-muted-foreground mt-1">
          Range: {spec.min ?? '-'} to {spec.max ?? '-'}
        </p>
      )}
    </div>
  );
}

export const QASettingsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<DraftMap>({});
  const [draft, setDraft] = useState<DraftMap>({});
  // Enabled LLM models grouped by lowercased provider key - drives the
  // Reviewer model dropdown (mirrors the LLM Routing tab).
  const [modelsByProvider, setModelsByProvider] = useState<Record<string, LlmModelSummary[]>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [resp, models] = await Promise.all([
        entityQAService.getSelfLearningSettings(),
        brainConfigService.listLlmModels(undefined, true).catch(() => [] as LlmModelSummary[]),
      ]);
      const grouped: Record<string, LlmModelSummary[]> = {};
      for (const m of models) {
        const key = (m.provider || '').toLowerCase();
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(m);
      }
      setModelsByProvider(grouped);
      const initial = buildInitialDraft(resp);
      setBaseline(initial);
      setDraft(JSON.parse(JSON.stringify(initial)));
    } catch (err) {
      logger.error('QASettingsTab load failed', err);
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(() => hasDraftChanges(draft, baseline), [draft, baseline]);

  const handleFieldChange = useCallback(
    (sectionKey: string, fieldName: string, value: string | number | boolean | null) => {
      setDraft((prev) => {
        const section = prev[sectionKey];
        const nextSection = {
          ...section,
          [fieldName]: { ...section[fieldName], value },
        };
        // Switching the Reviewer provider invalidates the previously-chosen
        // Reviewer model (it belongs to the old provider). Default to the
        // first configured model for the newly chosen provider so the field
        // is never left on a stale/invalid value.
        if (fieldName === 'reviewer_provider' && section['reviewer_model']) {
          const models = modelsByProvider[providerKeyFor(value)] || [];
          const defaultModel = models.length ? models[0].name : null;
          nextSection['reviewer_model'] = { ...section['reviewer_model'], value: defaultModel };
        }
        return { ...prev, [sectionKey]: nextSection };
      });
    },
    [modelsByProvider]
  );

  const handleReset = useCallback(() => {
    setDraft(JSON.parse(JSON.stringify(baseline)));
  }, [baseline]);

  const handleSave = useCallback(async () => {
    const payload = diffPayload(draft, baseline);
    if (!Object.keys(payload).length) {
      toast.info('No changes to save');
      return;
    }
    try {
      setSaving(true);
      const resp = await entityQAService.updateSelfLearningSettings(payload);
      if (resp.success) {
        toast.success(`Saved ${resp.applied_count} setting(s)`);
        await load();
      } else {
        const errSummary = resp.errors
          .map((e) => `${e.section}${e.field ? '.' + e.field : ''}: ${e.error}`)
          .join('; ');
        toast.error(`Save failed: ${errSummary}`);
      }
    } catch (err) {
      logger.error('QASettingsTab save failed', err);
      toast.error(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [draft, baseline, load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading settings…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-sm text-destructive mb-3">{error}</p>
          <Button variant="outline" onClick={load}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const sectionEntries = Object.entries(draft);

  return (
    <div className="space-y-6">
      {sectionEntries.map(([sectionKey, sectionDraft]) => {
        const meta = SECTION_TITLES[sectionKey] || { title: sectionKey, subtitle: '' };
        const baselineSection = baseline[sectionKey] || {};
        const sectionDirty = Object.keys(sectionDraft).some(
          (fn) => sectionDraft[fn].value !== baselineSection[fn]?.value
        );
        return (
          <Card key={sectionKey}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">{meta.title}</CardTitle>
                  <CardDescription className="mt-1">{meta.subtitle}</CardDescription>
                </div>
                {sectionDirty && (
                  <span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-400 whitespace-nowrap">
                    Unsaved
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {Object.entries(sectionDraft).map(([fieldName, fd], idx) => {
                // Reviewer provider / model render as dropdowns (like the LLM
                // Routing tab): provider from the fixed slug list, model from
                // the configured LLM models for the chosen provider.
                let selectOptions: SelectOption[] | undefined;
                let selectPlaceholder: string | undefined;
                if (fieldName === 'reviewer_provider') {
                  selectOptions = REVIEWER_PROVIDERS;
                  selectPlaceholder = 'Select a provider';
                } else if (fieldName === 'reviewer_model') {
                  const providerVal = sectionDraft['reviewer_provider']?.value;
                  const models = modelsByProvider[providerKeyFor(providerVal)] || [];
                  selectOptions = models.map((m) => ({
                    value: m.name,
                    label: m.model_name || m.name,
                  }));
                  selectPlaceholder = models.length
                    ? 'Select a model'
                    : 'No models configured for this provider';
                }
                return (
                  <React.Fragment key={fieldName}>
                    {idx > 0 && <Separator />}
                    <FieldRow
                      fieldName={fieldName}
                      draft={fd}
                      onChange={(v) => handleFieldChange(sectionKey, fieldName, v)}
                      selectOptions={selectOptions}
                      selectPlaceholder={selectPlaceholder}
                    />
                  </React.Fragment>
                );
              })}
            </CardContent>
          </Card>
        );
      })}

      <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-background py-3 border-t">
        <Button variant="ghost" onClick={handleReset} disabled={!dirty || saving}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset
        </Button>
        <Button onClick={handleSave} disabled={!dirty || saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save changes
        </Button>
      </div>
    </div>
  );
};
